import { Injectable } from '@angular/core';

import { Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { standardDeviation } from 'app/utils/statistics';

import { InSnapshots, OutSnapshots } from '../preciate-stream/types';
import { Keys } from '../social-pixi-canvas/keys';

import { VideoRoomService } from './video-room.service';

/**
 * Service with the purpose of providing a way to check RTC stats
 * It takes 5 snapshots with 1 second of difference and calculates the standard deviation
 * to understand the variation through each snapshot.
 *
 * Instructions on how to retrieve the stats can be found here:
 * https://gitlab.com/preciate/docs/-/blob/master/guidelines/social-webrtc-stats.md
 *
 */

@Injectable({
  providedIn: 'root',
})
export class RtcStatsService extends Loggable {
  private overUserId?: string;
  private statInterval?: Timeout;
  private inSnapshots: InSnapshots;
  private outSnapshots: OutSnapshots;

  public constructor(private videoRoomService: VideoRoomService) {
    super();
    this.setDisplayName('RtcStatsService');

    this.inSnapshots = {
      bytesReceived: [],
      headerBytesReceived: [],
      packetsReceived: [],
      packetsLost: [],
      framesReceived: [],
    };
    this.outSnapshots = {
      packetsSent: [],
      bytesSent: [],
      headerBytesSent: [],
      retransmittedBytesSent: [],
      retransmittedPacketsSent: [],
    };

    // Listener
    if (window) {
      window.addEventListener('keydown', this.keyDownFn);
    } else {
      this.error('RTC Stats: Not able to set listeners');
    }
  }

  public pointerOverUser(userId: string): void {
    this.overUserId = userId;
  }

  public pointerOutUser(): void {
    this.overUserId = undefined;
  }

  private keyDownFn = (ev: KeyboardEvent): void => {
    // Trigger Output Rtc Snapshot Stats
    if ((Keys.KEY_A === ev.code || Keys.KEY_V === ev.code) && ev.shiftKey && ev.ctrlKey && ev.altKey) {
      this.resetStats();

      // Define if stats are for audio or video based on the keys pressed
      const kind = Keys.KEY_V === ev.code ? 'video' : 'audio';

      // Snapshot is for User Participant
      if (this.overUserId) {
        const connection = this.videoRoomService.getParticipant(this.overUserId)?.connection;
        this.info(`=== RTC Stats: Participant ${this.overUserId} - ${kind.toUpperCase()} Snapshot ===`);
        if (!connection) {
          this.info('RTC Stats: No peer no stats');
          return;
        }
        // Schedule the snapshots
        let cycles = 0;
        this.statInterval = setInterval(async () => {
          await this.takeInputSnapshot(connection, kind, cycles);
          // On hte last cycle print the Standard Deviation of the most significant stats
          if (cycles >= 4) {
            this.finishInterval(this.inSnapshots);
          }
          cycles++;
        }, 1000);

        // Snapshot is for My Player
      } else {
        const { peer } = this.videoRoomService;
        this.info(`=== RTC Stats: MY ${kind.toUpperCase()} Snapshot ===`);
        if (!peer) {
          this.info('RTC Stats: No peer no stats');
          return;
        }
        // Schedule the snapshots
        let cycles = 0;
        this.statInterval = setInterval(async () => {
          await this.takeOutputSnapshot(peer, kind, cycles);
          // On hte last cycle print the Standard Deviation of the most significant stats
          if (cycles >= 4) {
            this.finishInterval(this.outSnapshots);
          }
          cycles++;
        }, 1000);
      }
    }
  };

  private async takeInputSnapshot(peer: RTCPeerConnection, kind = 'audio', cycles: number): Promise<void> {
    if (!peer || peer.connectionState !== 'connected') {
      return;
    }
    const stats = await peer.getStats();
    if (!stats) {
      return;
    }
    stats.forEach((rep) => {
      if (rep.type === 'inbound-rtp' && rep.kind === kind) {
        this.info(`RTC IN> - ${kind} Stats Snapshot #${cycles + 1}:`, rep);

        // Log the snapshot values in array for future average or standard deviation calculations
        this.inSnapshots.bytesReceived.push(rep.bytesReceived);
        this.inSnapshots.headerBytesReceived.push(rep.headerBytesReceived);
        this.inSnapshots.packetsReceived.push(rep.packetsReceived);
        this.inSnapshots.packetsLost.push(rep.packetsLost);
        if (kind === 'video') {
          this.inSnapshots.framesReceived?.push(rep.framesReceived);
        }
      }
    });
  }

  private async takeOutputSnapshot(peer: RTCPeerConnection, kind = 'audio', cycles: number): Promise<void> {
    if (!peer || peer.connectionState !== 'connected') {
      return;
    }
    const stats = await peer.getStats();
    if (!stats) {
      return;
    }
    stats.forEach((rep) => {
      if (rep.type === 'outbound-rtp' && rep.kind === kind) {
        this.info(`RTC OUT> - ${kind} Stats Snapshot #${cycles + 1}:`, rep);

        // Log the snapshot values in array for future average or standard deviation calculations
        this.outSnapshots.bytesSent.push(rep.bytesSent);
        this.outSnapshots.packetsSent.push(rep.packetsSent);
        this.outSnapshots.headerBytesSent.push(rep.headerBytesSent);
        this.outSnapshots.retransmittedBytesSent.push(rep.retransmittedBytesSent);
        this.outSnapshots.retransmittedPacketsSent.push(rep.retransmittedPacketsSent);
      }
    });
  }

  private finishInterval(snapshot: InSnapshots | OutSnapshots): void {
    for (const [key, values] of Object.entries(snapshot)) {
      if (values.length > 0) {
        const val = standardDeviation(values);
        this.info(`STANDARD DEVIATION ${key}: ${val}`);
      }
    }
    this.info('====== END ======');

    // Reset stats and snapshots interval
    this.resetStats();
  }

  private resetStats(): void {
    if (this.statInterval) {
      clearInterval(this.statInterval);
    }
    this.inSnapshots = {
      bytesReceived: [],
      headerBytesReceived: [],
      packetsReceived: [],
      packetsLost: [],
      framesReceived: [],
    };
    this.outSnapshots = {
      packetsSent: [],
      bytesSent: [],
      headerBytesSent: [],
      retransmittedBytesSent: [],
      retransmittedPacketsSent: [],
    };
  }

  public destroy(): void {
    window.removeEventListener('keydown', this.keyDownFn);
  }
}
